import { TeamMember } from "../../types/adminConsoleTypes";
import API from "Classes/Api";
import { ApiPostCallParams, ApiGetCallParams, ApiPatchCallParams } from "Classes/ApiCallParams";
import { AuditUserAction } from "types/app";

class Users extends API {
  async getOrganizationUsers(clientId: number) {
    const query = {};

    const response = await super.call(
      new ApiGetCallParams({
        action: `organization/${clientId}/users`,
        query,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return response;
  }

  async getAllowedProfilesForAssignment(
    clientId: number,
    userId: number
  ): Promise<{ data: { [id: number]: string } }> {
    const query = {};

    const response = await super.call(
      new ApiGetCallParams({
        action: `organization/${clientId}/users/${userId}/allowed_profiles_for_assignment`,
        query,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return response;
  }

  async create(clientId: number, user: number) {
    const response = await super.call(
      new ApiPostCallParams({
        action: `organization/${clientId}/users`,
        body: { data: user }, // api looks for body.data
        headers: { ...API.getAuthHeaders() },
      })
    );

    return response;
  }

  async invite(clientId: number, payload: any) {
    const response = await super.call(
      new ApiPostCallParams({
        action: `v2/organization/${clientId}/users/invite`,
        body: payload,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return response;
  }

  async updateSelf(user: TeamMember) {
    const response = await super.call(
      new ApiPatchCallParams({
        action: `users/self`,
        body: { data: user }, // api looks for body.data
        headers: { ...API.getAuthHeaders() },
        successMessage: "Profile successfully saved.",
        failureMessage: "Failed to save profile.",
      })
    );

    return response;
  }

  async update(clientId: number, userId: number, user: TeamMember) {
    const response = await super.call(
      new ApiPatchCallParams({
        action: `organization/${clientId}/users/${userId}`,
        body: { data: user }, // api looks for body.data
        headers: { ...API.getAuthHeaders() },
        successMessage: "Profile successfully saved.",
        failureMessage: "Failed to save profile.",
      })
    );

    return response;
  }

  async addUser(user: any) {
    const data = { ...user };
    delete data.id;
    delete data.userID;
    delete data.roleName;

    data.roleID = parseInt(data.roleID, 10) || null;
    data.teamID = parseInt(data.teamID, 10) || null;

    const result = await super.call(
      new ApiPostCallParams({
        action: `users`,
        body: { data }, // api looks for body.data
        headers: { ...API.getAuthHeaders() },
      })
    );

    return result;
  }

  async removeUsers(clientId: number, users: number[]) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `organization/${clientId}/team_members/delete`,
        headers: { ...API.getAuthHeaders() },
        body: { data: users },
      })
    );

    return result;
  }

  async deactivateUser(clientId: number, userId: number) {
    const result = await this.deactivateUsers(clientId, [userId]);

    return result;
  }

  async deactivateUsers(clientId: number, users: number[]) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `organization/${clientId}/team_members/deactivate`,
        headers: { ...API.getAuthHeaders() },
        body: { data: users },
      })
    );

    return result;
  }

  async removeUser(clientId: number, userId: number) {
    const result = await this.removeUsers(clientId, [userId]);

    return result;
  }

  async reactivateUser(clientId: number, userId: number) {
    const result = await this.reactivateUsers(clientId, [userId]);
    return result;
  }

  async reactivateUsers(clientId: number, users: number[]) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `organization/${clientId}/team_members/invite_and_activate`,
        headers: { ...API.getAuthHeaders() },
        body: { data: users },
      })
    );

    return result;
  }

  async changePassword(user: {
    confirmPassword1: string;
    currentPassword: string;
    userID: number;
  }) {
    const data = {
      currentPassword: user.currentPassword,
      newPassword: user.confirmPassword1,
    };

    const result = await super.call(
      new ApiPostCallParams({
        action: `users/${user.userID}/change_password`,
        headers: { ...API.getAuthHeaders() },
        body: { data },
        successMessage: "New Password successfully changed.",
        failureMessage: "Failed to change password.",
      })
    );

    return result;
  }

  async audit(userId: number, data: AuditUserAction) {
    await super.call(
      new ApiPostCallParams({
        action: `users/${userId}/audit`,
        headers: { ...API.getAuthHeaders() },
        body: { data },
      })
    );
  }
}

const instance = new Users();
export default instance;
